/**
 * Global context for shopping cart
 * To access global context: 
 * const { addToCart, incrementQuantity, decrementQuantity, totalItem, totalQty, cartItems, removeFromCart, emptyCart } = useShoppingCart();
 */
import React, { createContext, useState, useContext, useEffect } from 'react';

const ShoppingCartContext = createContext();

export function ShoppingCartProvider({ children }) {

  const [cartItems, setCartItems] = useState(() => {
    const storedCartItems = sessionStorage.getItem('cartItems');
    return storedCartItems ? JSON.parse(storedCartItems) : [];
  });

  const [cartOptions, setCartOptions] = useState(() => {
    const storedCartOptions = sessionStorage.getItem('cartOptions');
    return storedCartOptions ? JSON.parse(storedCartOptions) : {};
  });

  // Saving my shopping cart
  useEffect(() => {
    sessionStorage.setItem('cartItems', JSON.stringify(cartItems));
    sessionStorage.setItem('cartOptions', JSON.stringify(cartOptions));
  }, [cartItems, cartOptions]);


  const loadServerCartItems = (newCartItems) => {
    setCartItems([...newCartItems]);
    sessionStorage.setItem('cartItems', JSON.stringify(newCartItems));
  }

  const loadServerCartOptions = (newCartOptions) => {
    setCartOptions({ ...newCartOptions });
    sessionStorage.setItem('cartOptions', JSON.stringify(newCartOptions));
  }

  /**
   *  Add an item to shopping cart
   * {
   *   "id": 1, - cartId
   *   "preorder_id": 1
   *   "preorder":{}
   *   "product_id": 1
   *   "product": {}
   *   "qunatity":
   *   "tax_amount":
   * }
   */
  const addToCart = (item) => {
    const existingItem = cartItems.find((cartItem) => cartItem.id === item.id);
    if (existingItem) {
      setCartItems((prevCartItems) =>
        prevCartItems.map((cartItem) =>
          cartItem.id === item.id ? { ...cartItem, quantity: parseInt(item.quantity) } : cartItem
        )
      );
    } else {
      setCartItems((prevCartItems) => {
        prevCartItems.push(item);
        return [...prevCartItems ]
      });
    }
  };

  /**
   * Update cartitem
   * @param {*} itemId 
   * @param {*} data 
   */
  const updateCartItem = (itemId, data) => {
    setCartItems((prevCartItems) =>
      prevCartItems.map((item) =>
        item.id === itemId ? { ...item, ...data } : item
      )
    );
  };

  /**
   * Update cart option. delivery: { }, 
   * @param string optionName - deliveryMethod, deliveryAddress, billingAddress, paymentMethod, orderCharge
   * @param {*} value 
   */
  const updateCartOption = (optionName, value) => {
    setCartOptions({ ...cartOptions, [optionName]: value } )
  }

  /**
   * Get Cart item
   * @param int cartId 
   * @returns 
   */
  const itemInCart = (itemId) => {
    return cartItems.find((cartItem) => cartItem.id === itemId);
  }

  /**
   * Remove an item in the cart using cartId
   * @param int itemId 
   */
  const removeFromCart = (itemId) => {
    const itemIndex = cartItems.findIndex((item) => item.id == itemId);

    if (itemIndex !== -1) {
      const newCartItems = [...cartItems];
      newCartItems.splice(itemIndex, 1);
      setCartItems(newCartItems);
    }
  };

  const setQuantity = (itemId, qnty) => {

    setCartItems((prevCartItems) =>
      prevCartItems.map((item) => 
        item.id === itemId ? { ...item, quantity: qnty } : item
      )
    );
  };

  // Calculate cart total
  const getTotals = () => {
    let accumulate = {
      itemTotal: 0,
      taxTotal: 0,
      deliveryTotal: 0,
      pickFeeTotal: 0,
      orderCharge: 0,
      orderTotal: 0
    }
    if (cartItems) {
      cartItems.map((item, idx) => {
        accumulate.itemTotal += (item.subtotal ? item.subtotal : 0);
        accumulate.taxTotal += (item.tax_amount ? item.tax_amount : 0);
        accumulate.pickFeeTotal += (item.pick_fee ? item.pick_fee : 0);
      });

      if (cartOptions.delivery_cost) {
        accumulate.delivery_cost = cartOptions.delivery_cost;
      }
      
      accumulate.orderCharge = (cartOptions.order_charge ? cartOptions.order_charge : 0);

      accumulate.orderTotal = (accumulate.itemTotal + accumulate.taxTotal +
      accumulate.deliveryTotal + accumulate.pickFeeTotal + accumulate.orderCharge);
    }
    return accumulate;
  };


  const emptyCart = () => {
    setCartItems([]);
  };

  return (
    <ShoppingCartContext.Provider
      value={{
        cartItems,
        loadServerCartItems,
        loadServerCartOptions,
        addToCart,
        updateCartItem,
        itemInCart,
        removeFromCart,
        setQuantity,
        getTotals,
        emptyCart,
        cartOptions,
        updateCartOption
      }}
    >
      {children}
    </ShoppingCartContext.Provider>
  );
}

export function useShoppingCart() {
  return useContext(ShoppingCartContext);
}
